import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addOrUpdateStrain,
  getAllFlavors,
  getAllInfoEffect,
} from "../../../redux/slices/hyperwolf/strains";
import { validateStrainDraftData } from "validations/strain";
import { validateHyperwolfStrainData } from "validations/strain";
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Grid,
  FormHelperText,
  FormControl,
  FormGroup,
  Box,
  Card,
  Stack,
  Badge,
  Icon,
} from "@mui/material";
import CommonQuillEditor from "common/editor";
import { isEmptyObject } from "utilities/common";
import CustomCheckbox from "components/common/CustomCheckbox";
import CustomSelect from "components/common/CustomSelect";
import CustomMultiSelect from "components/common/CustomMultiSelect";
import CommonLayout from "layouts/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { IsheadingExistBlog, openSnackbar, uploadImage } from "../../../redux/slices/common";
import { slugRegex } from "utilities/common";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import defaultImage from "assets/images/placeholder-image.png";
import { grey } from "@mui/material/colors";
import DeleteIcon from "common/customIcons/delete";
import { useParams } from "react-router-dom";
import { getSingleStrain } from "../../../redux/slices/strain";
import { singleStrainDetailPagePrompt } from "../../../prompt";
import { convertAndExtractStrainMeta } from "utilities/common";
import SmartToyIcon from '@mui/icons-material/SmartToy';

const initialEmployee = {};

function AddHyperwolfStrain() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isUploading = false } = useSelector((state) => state.common);
  const {
    isLoading = false,
    infoEffects,
    allFlavorsData,
    singleStrain = {},
  } = useSelector((state) => state.hyperwolfStrains);
  const [strainData, setStrainData] = useState({});
  const [errors, setErrors] = useState({});
  const [selectedFlavors, setSelectedFlavors] = useState([]);
  const [productSlugData, setProductSlugData] = useState("");
  const [isProductSlug, setIsProductSlugData] = useState(false);
  const [faqError, setFaqError] = useState("");
  const [promptIsLoading, setPromptIsLoading] = useState(false);
  const { strainId } = useParams();
  const { user = {} } = useSelector(state => state.auth);

  const isEditdata = useMemo(() => {
    return !isEmptyObject(singleStrain);
  }, [singleStrain]);

  const infoEffectArr = useMemo(() => {
    return (
      infoEffects?.find((effect) => effect?.strainType === strainData.strainCategory)
        ?.infoEffects || []
    );
  }, [strainData.strainCategory, infoEffects]);

  const capitalizeString = (str) => {
    return str?.slice(0, 1)?.toUpperCase() + str?.slice(1)?.toLowerCase();
  };

  const isQuillContentEmpty = (html = "") => {
    const text = html
      .replace(/<(.|\n)*?>/g, "") // remove tags
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };

  useEffect(() => {
    if (singleStrain?.subStrainSlug) {
      setProductSlugData(singleStrain?.subStrainSlug);
    }
    if (singleStrain?.flavors?.length) {
      setSelectedFlavors([
        ...selectedFlavors,
        ...singleStrain.flavors?.map((flavour) => {
          return { name: flavour };
        }),
      ]);
    }
  }, [singleStrain]);

  const allFlavorsDataArr = useMemo(() => {
    return allFlavorsData?.length > 0
      ? allFlavorsData?.map((flavour) => {
        return { name: flavour.flavorType };
      })
      : [];
  }, [allFlavorsData]);

  const onChangeCheckbox = (e, name) => {
    e.preventDefault();
    let updatedEffects = [...(strainData?.infoEffects || [])];
    let alreadyExist = updatedEffects.findIndex((v) => v === name);
    if (alreadyExist === -1) {
      updatedEffects.push(name);
    } else {
      updatedEffects.splice(alreadyExist, 1);
    }
    setStrainData({ ...strainData, infoEffects: updatedEffects });
  };
  const debounceRef = useRef(null);

  useEffect(() => {
    if (isEditdata) {
      setStrainData({ ...singleStrain, flavors: singleStrain.flavors?.join(",") });
    }
  }, [isEditdata, singleStrain]);
  const handlePostTitleChange = (value) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      dispatch(IsheadingExistBlog({ heading: value, strain: true }))
        .unwrap()
        .then(() => {
          setErrors({ strainName: "" });
        })
        .catch((err) => {
          setErrors({ strainName: err?.message || "An error occurred" });
        });
    }, 500); // 500ms delay
  };
  const onChangehandler = (e) => {
    const { name, value } = e.target;
    if (name === "strainName") {
      handlePostTitleChange(value);
    }
    setStrainData({ ...strainData, [name]: value });
  };

  const onEditorChangehandler = (value, name) => {
    setStrainData({ ...strainData, [name]: value });
  };

  const isLastFAQFilled = () => {
    const faqs = strainData.faq || [];
    if (faqs.length === 0) return true;

    const last = faqs[faqs.length - 1];
    return Boolean(
      (last?.question && last.question.trim() !== "") || (last?.answer && last.answer.trim() !== "")
    );
  };

  const handleRemoveFAQ = (faQIdx) => {
    let FAQArray = [...(strainData?.faq || [])];
    FAQArray = FAQArray.filter((faqs, _idx) => _idx !== faQIdx);
    setStrainData({
      ...strainData,
      faq: FAQArray,
    });
  };

  const addFAQHandler = () => {
    if (!isLastFAQFilled()) {
      setFaqError("Please fill the previous FAQ before adding a new one.");
      return;
    }
    setFaqError("");
    let newFAQ = [...(strainData?.faq || [])];
    let faqItem = {};
    newFAQ.push(faqItem);
    setStrainData({
      ...strainData,
      faq: newFAQ,
    });
  };

  const handleChangeFAQ = (value, fieldType, idx) => {
    let FAQArray = [...(strainData?.faq || [])];
    if (FAQArray[idx]) {
      FAQArray[idx] = {
        ...FAQArray[idx],
        [fieldType]: value,
      };
    }
    setStrainData({
      ...strainData,
      faq: FAQArray,
    });
  };

  const handleGenerate = useCallback(async () => {
    setPromptIsLoading(true);
    setStrainData(prev => ({
      ...prev,
      description: '',
      metaTitle: '',
      metaDescription: '',
      title: '',
      title2: '',
      faq: []
    }));

    try {
      const strainName = strainData?.subStrainName || strainData?.strainName || 'Unknown Strain';
      const prompt = singleStrainDetailPagePrompt({ strainName, platform: "hyperwolf" });
      const payload = { prompt: prompt };
      const res = await fetch(`${process.env.REACT_APP_STILO_API_BASE_URL}/api/v1/generate/description`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_AUTH_KEY
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let fullContent = "";

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          fullContent += chunk;

          const finalResult = convertAndExtractStrainMeta(fullContent);

          setStrainData((prev) => ({
            ...prev,
            description: finalResult.longDescription,
            metaTitle: finalResult.metaTitle || prev.metaTitle,
            metaDescription: finalResult.metaDescription,
            title: finalResult.title || prev.title,
            title2: finalResult.title2 || prev.title2,
            faq: finalResult.faq || [],
          }));
        }
      } catch (readerError) {
        console.error('Reader error:', readerError);
        // If streaming fails, try to get the full content from what we have so far
        if (fullContent) {
          const finalResult = convertAndExtractStrainMeta(fullContent);
          setStrainData((prev) => ({
            ...prev,
            description: finalResult.longDescription,
            metaTitle: finalResult.metaTitle || prev.metaTitle,
            metaDescription: finalResult.metaDescription,
            title: finalResult.title || prev.title,
            title2: finalResult.title2 || prev.title2,
            faq: finalResult.faq || [],
          }));
        }
      }

    } catch (error) {
      console.error('Error generating content:', error);
    } finally {
      setPromptIsLoading(false);
    }
  }, [strainData?.strainName]);

  const showResponseMessage = (message, errortype) => {
    if (errortype === "success") {
      dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
    }
    if (errortype === "error") {
      dispatch(openSnackbar({ content: message, color: "error", icon: "warning" }));
    }
  };

  const addStrainHandler = (submitType) => {
    const { errors: validationErrors, isValid: isDataValid } =
      submitType === "save_as_draft"
        ? validateStrainDraftData(strainData)
        : validateHyperwolfStrainData(strainData);
    if (!isDataValid) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    let payload = {
      ...strainData,
      isReviewed: false,
      faq: strainData.faq?.filter(
        (f) => !isQuillContentEmpty(f?.question) || !isQuillContentEmpty(f?.answer)
      ),
    };

    if (isProductSlug) {
      payload.subStrainSlug = `${productSlugData}`;
    } else {
      delete payload.subStrainSlug;
    }
    const userForPayload = {
      ...payload,
      createdBy: strainData.createdBy ? strainData.createdBy : user?.name,
      updatedBy: strainData.updatedBy ? strainData.updatedBy : user?.name,
      status: submitType === "save_as_draft" ? false : true,
      flavors: selectedFlavors?.map((flavour) => Object.values(flavour)[0]),
    };

    dispatch(addOrUpdateStrain({ data: userForPayload, id: strainData._id || "", }))
      .unwrap()
      .then((res) => {
        showResponseMessage(res?.message, "success");
        navigate("/hyperwolf/manage_main_strain?strainTab=strains");
      })
      .catch((err) => {

        showResponseMessage(err?.message, "error");
      });
  };

 
  useEffect(() => {
    dispatch(getAllInfoEffect());
    dispatch(getAllFlavors());
  }, []);

  useEffect(() => {
    if (!strainId) return;
    dispatch(getSingleStrain(strainId)).unwrap().then((res) => {
      setStrainData({ ...res?.strainDetails })
      if (res?.strainDetails?.subStrainSlug) {
        setProductSlugData(res?.strainDetails?.subStrainSlug)
      }
    })
      .catch((err) => {
        showResponseMessage(err?.message, "error");
      })
  }, [strainId]);

  const handleChangeFlavor = (e, val) => {
    setSelectedFlavors(val);
  };

  const handleChangeSlug = (e) => {
    const value = e.target.value;
    setIsProductSlugData(true);
    setProductSlugData(slugRegex(value));
  };



  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: "none" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" className="d-flex jc-ctr" mb={2}>
              {singleStrain?.metaTitle ? "Edit" : "Add"} Strain
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <MDButton
                type="button"
                color="primary"
                size="medium"
                variant="outlined"
                sx={{ minWidth: "180px" }}
                onClick={handleGenerate}
                disabled={promptIsLoading || !strainData?.strainName}
              >
                {promptIsLoading ? (
                  <>
                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                    Generating...
                  </>
                ) : (
                  <>
                    <SmartToyIcon sx={{ mr: 1 }} />
                    Generate Content
                  </>
                )}
              </MDButton>
              {/* <Typography variant="caption" color="text.secondary">
                    AI will generate meta title, description, title, title2, and FAQ based on strain name
                  </Typography> */}
            </Stack>
          </Box>
          <Grid container spacing={4} component="main" className={"add-zipcode-modal-container "}>
            <Grid item xs={12} className="add-category-input">
              <Grid item xs={12}>
                <CustomSelect
                  name="strainCategory"
                  value={strainData.strainCategory || ""}
                  menuItem={
                    infoEffects?.map((effect) => ({
                      title: effect.strainType,
                      value: effect.strainType,
                    })) || []
                  }
                  onChange={onChangehandler}
                  label="Strain Category"
                  className="w-100"
                  margin="normal"
                  error={errors.strainCategory}
                />

                {errors?.strainCategory && (
                  <FormHelperText error>{errors?.strainCategory}</FormHelperText>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="strainName"
                  value={strainData.strainName || ""}
                  onChange={onChangehandler}
                  label="Strain Name"
                  className="w-100"
                  margin="normal"
                  error={errors.strainName}
                  helperText={errors.strainName}
                />
              </Grid>
              <Grid item xs={12} mt={1}>
                <TextField
                  label="Strain Heading"
                  value={strainData.strainHeading}
                  fullWidth
                  variant="outlined"
                  onChange={onChangehandler}
                  name="strainHeading"
                  error={Boolean(errors?.strainHeading)}
                  helperText={errors?.strainHeading}
                />
              </Grid>
              {singleStrain && (
                <Grid item xs={12} mt={2}>
                  <TextField
                    className="form-input"
                    fullWidth
                    label="Strain Slug"
                    name="subStrainSlug"
                    value={productSlugData}
                    onChange={handleChangeSlug}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  name="metaTitle"
                  value={strainData.metaTitle || ""}
                  onChange={onChangehandler}
                  label="Meta Title"
                  className="w-100"
                  margin="normal"
                  error={errors.metaTitle}
                  helperText={errors.metaTitle}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="metaDescription"
                  value={strainData.metaDescription || ""}
                  onChange={onChangehandler}
                  label="Meta Description"
                  className="w-100"
                  margin="normal"
                  error={errors.metaDescription}
                  helperText={errors.metaDescription}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormHelperText style={{ fontSize: "14px" }}>Description</FormHelperText>
                  <CommonQuillEditor
                    onChange={(value) => setStrainData({ ...strainData, description: value })}
                    id={"description"}
                    allowHeading={false}
                    value={strainData.description}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormHelperText style={{ fontSize: "14px" }}>Title</FormHelperText>
                  <CommonQuillEditor
                    onChange={(value) => setStrainData({ ...strainData, title: value })}
                    id={"title"}
                    allowHeading={false}
                    value={strainData.title}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                {infoEffectArr?.length ? (
                  <FormGroup className="effects-listing">
                    <Grid container>
                      {infoEffectArr?.length
                        ? infoEffectArr?.map((item, index) => {
                          const { title } = item;
                          const capatlizedTitle = capitalizeString(title);
                          let value = strainData?.infoEffects?.includes(title);
                          return (
                            <Grid key={index} item md={3} className="innerGridstyle">
                              <div
                                key={index}
                                className={`effect-chkbox ${value ? "active" : ""}`}
                                onClick={(e) => onChangeCheckbox(e, title)}
                              >
                                {/* <img
                                  src={
                                    `${process.env.REACT_APP_AMZAON_S3_URL}infoEffects${item.url}` ||
                                    "/images/ProductEffectImage/default.svg"
                                  }
                                  alt={item.url || "default_image"}
                                /> */}
                                <CustomCheckbox label={capatlizedTitle} value={value || false} />
                              </div>
                            </Grid>
                          );
                        })
                        : null}
                    </Grid>
                  </FormGroup>
                ) : null}
              </Grid>
              <hr></hr>
              <Grid item xs={12}>
                <Grid container className="mb-2">
                  <Grid item xs={3}>
                    <CustomCheckbox
                      label={"Best Strain"}
                      value={strainData?.isBestStrain || false}
                      onChange={(e) => onEditorChangehandler(e.target.checked, "isBestStrain")}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomCheckbox
                      label={"Popular Strain"}
                      value={strainData.isPopularStrain || false}
                      onChange={(e) => onEditorChangehandler(e.target.checked, "isPopularStrain")}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <CustomMultiSelect
                  label="Flavors"
                  name="name"
                  fullWidth
                  options={allFlavorsDataArr || []}
                  handleChange={(e, val) => handleChangeFlavor(e, val)}
                  value={selectedFlavors || []}
                  className="w-50"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="createdBy"
                  value={strainData.createdBy || ""}
                  onChange={onChangehandler}
                  label="Created By"
                  className="w-100"
                  margin="normal"
                  error={errors.createdBy}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormHelperText style={{ fontSize: "14px" }}>Title 2</FormHelperText>
                  <CommonQuillEditor
                    onChange={(value) => setStrainData({ ...strainData, title2: value })}
                    id={"title2"}
                    allowHeading={false}
                    value={strainData.title2}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" fontWeight="semibold" mb={2} mt={4}>
                  FAQs
                </Typography>
                {strainData?.faq?.length > 0 ? (
                  <>
                    {strainData?.faq?.map((faqs, idx) => {
                      return (
                        <Grid key={idx} item xs={12} sm={12} lg={12} my={2}>
                          <Box width={"100%"} className="faq-sec-rep">
                            <Box
                              display="flex"
                              flexDirection="column"
                              justifyContent="space-between"
                              alignItems={"flex-start"}
                              className="mb-3"
                            >
                              <Box
                                width={"100%"}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="medium"
                                  color={grey[600]}
                                >
                                  Question
                                </Typography>
                                <MDButton
                                  sx={{ gap: 0.5 }}
                                  size="small"
                                  variant="text"
                                  onClick={() => handleRemoveFAQ(idx)}
                                  style={{ color: "#fd4438" }}
                                >
                                  <DeleteIcon
                                    width="1rem"
                                    height="1rem"
                                    style={{ color: "#fd4438" }}
                                  />{" "}
                                  Delete
                                </MDButton>
                              </Box>
                              <Box width={"100%"}>
                                <CommonQuillEditor
                                  allowHeading={false}
                                  allowImage={false}
                                  value={faqs?.question || ""}
                                  onChange={(value) => {
                                    handleChangeFAQ(value, "question", idx);
                                    setFaqError("");
                                  }}
                                />
                              </Box>
                            </Box>
                            <Box display="flex" justifyContent="center" alignItems={"flex-start"}>
                              <Box width={"100%"}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="medium"
                                  color={grey[600]}
                                  mb={1}
                                >
                                  Answer
                                </Typography>
                                <CommonQuillEditor
                                  allowHeading={false}
                                  allowImage={false}
                                  value={faqs?.answer || ""}
                                  onChange={(value) => {
                                    handleChangeFAQ(value, "answer", idx);
                                    setFaqError("");
                                  }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </>
                ) : (
                  <Grid item xs={12} sm={12} lg={12} mt={3}>
                    <Box display="flex" justifyContent="space-between" alignItems={"center"}>
                      <MDButton
                        type="button"
                        color="primary"
                        size="medium"
                        variant="outlined"
                        sx={{ minWidth: "180px" }}
                        onClick={addFAQHandler}
                      >
                        <Icon>add</Icon>&nbsp;Add FAQ
                      </MDButton>
                    </Box>
                  </Grid>
                )}
                {faqError && (
                  <Typography color="error" variant="body2" mb={1}>
                    {faqError}
                  </Typography>
                )}

                {strainData?.faq?.length > 0 && (
                  <Grid item xs={12} mt={3}>
                    <MDButton
                      type="button"
                      color="primary"
                      size="medium"
                      variant="outlined"
                      sx={{ minWidth: "180px" }}
                      onClick={addFAQHandler}
                    >
                      <Icon>add</Icon>&nbsp;Add More
                    </MDButton>
                  </Grid>
                )}
              </Grid>

              {/* <Grid item xs={12} >
                <input type='file' name='filePath' onChange={imageChangeHandler} />
                {
                  strainData?.image && <img style={{ height: 100, width: 200 }} src={strainData?.image} />
                }
                {
                  errors.image && <FormHelperText><div className='error' >{errors.image}</div></FormHelperText>
                }
              </Grid> */}
              {/* <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.25} mb={5}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'top', horizontal: 'right', }}
                  badgeContent={
                    isUploading ? <CircularProgress size={32} color='primary' sx={badgeCircularProgressIcon} /> : ''
                  }
                >
                  <img style={uploadThumbnailImage} src={strainData?.image || defaultImage} alt="" />
                </Badge>
                <label htmlFor="image-input">
                  <MDButton
                    sx={{ minWidth: '180px' }}
                    type='button'
                    variant='outlined'
                    color='secondary'
                    component='span'
                    disabled={isUploading}
                  >
                    Change Image
                  </MDButton>
                </label>
                <MDInput
                  id="image-input"
                  type="file"
                  name="image"
                  onChange={imageChangeHandler}
                  fullWidth
                  disabled={isUploading}
                  style={{ display: 'none' }}
                />
                {Boolean(errors?.image) && <FormHelperText sx={{ width: '100%', ml: 0, mt: -.5 }} error>{errors?.image}</FormHelperText>}
              </Stack> */}

              <Grid item xs={12}>
                <Box my={4} className="d-flex jc-ctr add-button">
                  <Grid container spacing={4}>
                    <Grid item xs={6} lg={6}>
                      <Button
                        disabled={isUploading}
                        onClick={() => addStrainHandler("save")}
                        variant="contained"
                        color="primary"
                      >
                        {isLoading ? (
                          <CircularProgress size={22} style={{ color: "white" }} />
                        ) : (
                          "Publish"
                        )}
                      </Button>
                    </Grid>
                    <Grid item xs={6} lg={6}>
                      <Box display="flex" justifyContent="flex-end" alignItems="center">
                        <Button
                          disabled={isUploading}
                          onClick={() => addStrainHandler("save_as_draft")}
                          variant="contained"
                          style={{
                            background: "#ff0000ad",
                            color: "white",
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          {isLoading ? (
                            <CircularProgress size={22} style={{ color: "white" }} />
                          ) : (
                            "Save as Draft"
                          )}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Card>
      </Box>
    </CommonLayout>
  );
}

export default AddHyperwolfStrain;